import { Formik } from 'formik'
import { isEmpty } from 'lodash'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'

import { Button } from 'components/v2/Button'
import { Input } from 'components/Input'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Row } from '@10xLabs/react-web/components/Row'
import { Column } from '@10xLabs/react-web/components/Column'
import Heading from 'components/v2/Heading'
import { InputFile } from '@10xLabs/react-web/components/InputFile'
import { InputNumber } from 'components/InputNumber'
import { TEMPLATE_NAMES } from 'features/Snacks/components/TemplateName/constants'
import { Label } from 'components/v2/Label'
import { Switch } from 'components/v2/Switch'
import { InputCurrency } from 'components/InputCurrency'
import FormLayout from 'features/App/components/FormLayout'
import { InputBrandSuggestion } from 'features/Snacks/components/InputBrandSuggestion'
import { InputCategorySuggestion } from 'features/Snacks/components/InputCategorySuggestion'
import { InputSubcategorySuggestion } from 'features/Snacks/components/InputSubcategorySuggestion'
import { InputUnitsSuggestion } from 'features/Snacks/components/InputUnitsSuggestion'
import { SelectContainer } from 'features/Snacks/components/SelectContainer'
import { schema } from './schema'
import { getUnprocessableErrors } from 'utils/error'
import { sanitizePrice } from 'utils/math'

const messages = defineMessages({
  namePlaceholder: {
    id: 'spanish.dictionary.name',
    defaultMessage: 'Nombre'
  },
  brandPlaceholder: {
    id: 'items.new.brand.placeholder',
    defaultMessage: 'E.g.: Bimbo'
  },
  unitsPlaceholder: {
    id: 'items.new.units.placeholder',
    defaultMessage: 'E.g.: mg'
  },
  netContentPlaceholder: {
    id: 'spanish.dictionary.netContent',
    defaultMessage: 'Contenido neto'
  },
  categoryPlaceholder: {
    id: 'items.new.category.placeholder',
    defaultMessage: 'E.g.: Alimentos'
  },
  subcategoryPlaceholder: {
    id: 'items.new.subcategory.placeholder',
    defaultMessage: 'E.g.: Galletas'
  }
})

const NewItemForm = (props: TSFixMe): TSFixMe => {
  const {
    values,
    touched,
    errors,
    handleChange,
    setFieldValue,
    handleBlur,
    handleSubmit,
    error,
    loading,
    isEdit,
    companyId,
    s3Error
  } = props

  const intl = useIntl()

  const handleFileChange = (file: TSFixMe): TSFixMe => {
    setFieldValue('photo', file)
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <FormLayout.CardContent>
        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="items.form.general.title" defaultMessage="General" />
            </Heading.H5>
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Input
              id="name"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              placeholder={intl.formatMessage(messages.namePlaceholder)}
              touched={touched.name}
              errors={errors.name}
              label={<FormattedMessage id="spanish.dictionary.name" defaultMessage="Nombre" />}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <InputBrandSuggestion
              id="brand"
              onChange={(brand: TSFixMe): TSFixMe => setFieldValue('brand', brand)}
              onBlur={handleBlur}
              value={values.brand}
              companyId={companyId}
              placeholder={intl.formatMessage(messages.brandPlaceholder)}
              label={<FormattedMessage id="spanish.dictionary.brand" defaultMessage="Marca" />}
              touched={touched.brand}
              errors={errors.brand}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <InputFile
              value={values.photo}
              label={<FormattedMessage id="spanish.dictionary.photo" defaultMessage="Foto" />}
              selectFileLabel={
                <FormattedMessage id="spanish.dictionary.selectFile" defaultMessage="Seleccionar archivo" />
              }
              onChange={handleFileChange}
              touched={touched.photo}
              errors={errors.photo}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <InputCategorySuggestion
              id="category"
              onChange={(category: TSFixMe): TSFixMe => setFieldValue('category', category)}
              onBlur={handleBlur}
              value={values.category}
              companyId={companyId}
              placeholder={intl.formatMessage(messages.categoryPlaceholder)}
              label={<FormattedMessage id="spanish.dictionary.category" defaultMessage="Categoría" />}
              touched={touched.category}
              errors={errors.category}
            />
          </Column>
          <Column width="50%">
            <InputSubcategorySuggestion
              id="subcategory"
              onChange={(subcategory: TSFixMe): TSFixMe => setFieldValue('subcategory', subcategory)}
              onBlur={handleBlur}
              value={values.subcategory}
              companyId={companyId}
              placeholder={intl.formatMessage(messages.subcategoryPlaceholder)}
              label={<FormattedMessage id="spanish.dictionary.subcategory" defaultMessage="Subcategoría" />}
              touched={touched.subcategory}
              errors={errors.subcategory}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <InputNumber
              id="netContent"
              value={values.netContent}
              placeholder="0"
              onChange={(value: TSFixMe): TSFixMe => {
                setFieldValue('netContent', value)
              }}
              onBlur={handleBlur}
              touched={touched.netContent}
              errors={errors.netContent}
              maskOptions={{
                suffix: '',
                prefix: '',
                allowDecimal: false
              }}
              label={
                <FormattedMessage
                  id="spanish.items.form.netContent.label"
                  defaultMessage="Contenido neto ({units})"
                  values={{
                    units: values.units
                  }}
                />
              }
            />
          </Column>
          <Column width="50%">
            <InputUnitsSuggestion
              id="units"
              onChange={(units: TSFixMe): TSFixMe => setFieldValue('units', units)}
              onBlur={handleBlur}
              value={values.units}
              companyId={companyId}
              placeholder={intl.formatMessage(messages.unitsPlaceholder)}
              label={<FormattedMessage id="spanish.dictionary.units" defaultMessage="Unidades" />}
              touched={touched.units}
              errors={errors.units}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <SelectContainer
              id="container"
              value={values.container}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.container}
              errors={errors.container}
              label={<FormattedMessage id="spanish.items.container.title" defaultMessage="Tipo de envase" />}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Label>
              <FormattedMessage
                id="spanish.items.form.needsRefrigeration.label"
                defaultMessage="Se necesita refrigerar"
              />
            </Label>
            <Switch
              value={values.needsRefrigeration}
              onChange={(value): TSFixMe => setFieldValue('needsRefrigeration', value)}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Label>
              <FormattedMessage id="spanish.items.form.isSim.label" defaultMessage="Es un SIM?" />
            </Label>
            <Switch
              value={values.isSim}
              disabled={isEdit}
              onChange={(value): TSFixMe => setFieldValue('isSim', value)}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <InputCurrency
              id="sellingPrice"
              label={<FormattedMessage id="spanish.dictionary.sellingPrice" defaultMessage="Precio de venta" />}
              value={values.sellingPrice}
              placeholder="$0.00"
              onChange={(value): TSFixMe => {
                setFieldValue('sellingPrice', value)
              }}
              onBlur={handleBlur}
              touched={touched.sellingPrice}
              errors={errors.sellingPrice}
              hideSuffix
            />
          </Column>
          <Column width="50%">
            <InputCurrency
              id="minSellingPrice"
              label={
                <FormattedMessage
                  id="spanish.items.new.minSellingPrice"
                  defaultMessage="Precio minimo <small>(Optional)</small>"
                  values={{
                    small: (msg): TSFixMe => <small>{msg}</small>
                  }}
                />
              }
              value={values.minSellingPrice}
              placeholder="$0.00"
              onChange={(value): TSFixMe => {
                setFieldValue('minSellingPrice', value)
              }}
              onBlur={handleBlur}
              hideSuffix
            />
          </Column>
        </Row>
      </FormLayout.CardContent>
      <FormLayout.Split />
      <FormLayout.CardFooter>
        {!isEmpty(s3Error) && (
          <div>
            <ErrorMessage>{s3Error}</ErrorMessage>
          </div>
        )}
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="spanish.products.form.error"
                defaultMessage="Hubo un error al agregar su producto"
              />
            </ErrorMessage>
          </div>
        )}
        <FormLayout.CardFooterActions>
          <Button as={NavLink} to="/snacks/products" kind="cancel">
            <FormattedMessage id="spanish.dictionary.cancel" defaultMessage="Cancelar" />
          </Button>
          <Button type="submit" kind="primary" loading={loading}>
            {isEdit ? (
              <FormattedMessage id="spanish.dictionary.update" defaultMessage="Editar" />
            ) : (
              <FormattedMessage id="spanish.dictionary.add" defaultMessage="Agregar" />
            )}
          </Button>
        </FormLayout.CardFooterActions>
      </FormLayout.CardFooter>
    </form>
  )
}

export const NewItem = ({ item = {}, companyId, onSubmit, error, loading, isEdit, s3Error }: TSFixMe): TSFixMe => {
  const intl = useIntl()

  const initialItem = {
    name: item.name || '',
    brand: item.brand || '',
    photo: item.photoPath || '',
    netContent: item.netContent ? item.netContent.toString() : '',
    units: item.units || '',
    category: item.category || '',
    subcategory: item.subcategory || '',
    container: item.container || '',
    needsRefrigeration: item.needsRefrigeration || false,
    isSim: item.templateName === TEMPLATE_NAMES.SIM,
    sellingPrice: item.sellingPrice ? sanitizePrice(item.sellingPrice / 100) : '',
    minSellingPrice: item.minSellingPrice ? sanitizePrice(item.minSellingPrice / 100) : ''
  }

  return (
    <Formik initialValues={initialItem} validationSchema={schema(intl)} onSubmit={onSubmit}>
      {(props): TSFixMe => {
        return (
          <NewItemForm
            {...props}
            companyId={companyId}
            isEdit={isEdit}
            error={error}
            s3Error={s3Error}
            loading={loading || props.isSubmitting}
          />
        )
      }}
    </Formik>
  )
}
